/**
 * logCapture — intercepts console.warn / console.error and window.onerror
 * to maintain a rolling buffer of recent diagnostic messages.
 *
 * Call initLogCapture() once at app startup (before React renders).
 * Then call getLogBuffer() / getLastStackTrace() when submitting feedback.
 */

const MAX_LINES = 50;

let _logBuffer = [];
let _lastStackTrace = null;
let _initialised = false;

function _appendLog(level, args) {
  const line = `[${level}] ${args
    .map((a) => {
      try {
        return typeof a === "object" ? JSON.stringify(a) : String(a);
      } catch {
        return String(a);
      }
    })
    .join(" ")}`;
  _logBuffer.push(line);
  if (_logBuffer.length > MAX_LINES) {
    _logBuffer.shift();
  }
}

export function initLogCapture() {
  if (_initialised || typeof window === "undefined") return;
  _initialised = true;

  // Patch console.warn
  const _origWarn = console.warn.bind(console);
  console.warn = (...args) => {
    _appendLog("WARN", args);
    _origWarn(...args);
  };

  // Patch console.error
  const _origError = console.error.bind(console);
  console.error = (...args) => {
    _appendLog("ERROR", args);
    _origError(...args);
  };

  // Capture unhandled JS errors
  window.onerror = (message, source, lineno, colno, error) => {
    const trace = error?.stack || `${message} (${source}:${lineno}:${colno})`;
    _lastStackTrace = trace;
    _appendLog("UNCAUGHT", [trace]);
  };

  // Capture unhandled promise rejections
  window.onunhandledrejection = (event) => {
    const reason = event.reason;
    const trace =
      reason instanceof Error
        ? reason.stack || reason.message
        : String(reason);
    _lastStackTrace = trace;
    _appendLog("UNHANDLED_REJECTION", [trace]);
  };
}

/** Returns the rolling buffer as a single newline-joined string. */
export function getLogBuffer() {
  return _logBuffer.join("\n");
}

/** Returns the last uncaught stack trace (or null). */
export function getLastStackTrace() {
  return _lastStackTrace;
}
